// Copilote Jamora — pure helpers of the bubble and the panel (streamed answers, safe rendering, availability).
// UI only: the server checks the right to ask on every question.
import { hasPermission } from "./permissions";

export const COPILOT_OPEN_EVENT = "copilot:open";
export const COPILOT_PAGE = "/copilote"; // the Copilote on its own page (the bubble is not shown there)
export const COPILOT_SUGGESTIONS = [
  "Quelles sont mes tâches en retard ?",
  "Quels prospects n'ont eu aucun échange depuis 30 jours ?",
  "Résume-moi le pipeline par étape.",
];

// The place the person is looking at, sent with each question so « cette page » means something. Never the screen content: the
// server-side assistant reads the data itself, with the person's own rights. Not sent from the Copilote's own page.
export function openPage(location) {
  if (!location || location.pathname.startsWith(COPILOT_PAGE)) return null;
  const address = `${location.pathname}${location.search || ""}`;
  return address.length <= 300 && /^\/(?!\/)[A-Za-z0-9_\-/.=?&%,:+]*$/.test(address) ? address : location.pathname.slice(0, 300);
}

export const PAGE_SUGGESTION = "Que montre cette page ? Résume l'essentiel.";

// Offered to whoever may use it: the module is on, the role carries « Utiliser le Copilote », the account switch is not off.
export function canOfferCopilot(user) {
  return Boolean(user?.features?.assistant) && hasPermission(user, "assistant:use") && user?.assistant_enabled !== false;
}

// Server-sent events: complete blocks (separated by a blank line) become events, the incomplete tail is kept for later.
export function parseSse(buffer) {
  const normalized = buffer.replace(/\r\n/g, "\n");
  const blocks = normalized.split("\n\n");
  const rest = blocks.pop();
  const events = [];
  for (const block of blocks) {
    const data = block.split("\n").filter((line) => line.startsWith("data:")).map((line) => line.slice(5).replace(/^ /, "")).join("\n");
    if (!data) continue;
    try {
      events.push(JSON.parse(data));
    } catch {
      // A malformed event is skipped rather than breaking the whole answer.
    }
  }
  return { events, rest };
}

// Only links INSIDE Jamora are clickable (a relative path); anything else stays plain text — an answer may quote data typed
// by people or forms, and must never become a link to an outside site.
export function isInternalLink(href) {
  // A backslash counts as a slash for browsers ("/\evil.example" opens another site), so none is accepted.
  return typeof href === "string" && href.startsWith("/") && !href.startsWith("//") && !/[\s<>"'\\\u0000-\u001f]/.test(href);
}

// Inline tokens of one line: **bold** and [label](href).
export function inlineTokens(text) {
  const tokens = [];
  const pattern = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let match;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) tokens.push({ type: "text", text: text.slice(last, match.index) });
    if (match[1] !== undefined) tokens.push({ type: "bold", text: match[1] });
    else if (isInternalLink(match[3])) tokens.push({ type: "link", text: match[2], href: match[3] });
    else tokens.push({ type: "text", text: match[2] });
    last = pattern.lastIndex;
  }
  if (last < text.length) tokens.push({ type: "text", text: text.slice(last) });
  return tokens;
}

const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_RULE = /^\s*\|?(\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?\s*$|^\s*\|(\s*:?-{3,}:?\s*\|)+\s*$/;

function tableCells(line) {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((cell) => inlineTokens(cell.trim()));
}

// Blocks of an answer: paragraphs, lists (« - », « * », « 1. »), tables (a header row, a « |---| » rule, then rows), headings rendered
// as bold paragraphs. Rows lines without the rule stay plain paragraphs.
export function answerBlocks(text) {
  const blocks = [];
  let list = null;
  const lines = (text || "").split("\n");
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trimEnd();
    if (TABLE_ROW.test(line) && TABLE_RULE.test(lines[index + 1] || "")) {
      const header = tableCells(line);
      const rows = [];
      index += 2;
      while (index < lines.length && TABLE_ROW.test(lines[index])) {
        const cells = tableCells(lines[index]);
        rows.push(header.map((_, i) => cells[i] || []));
        index += 1;
      }
      index -= 1;
      list = null;
      blocks.push({ type: "table", header, rows });
      continue;
    }
    const bullet = /^\s*(?:[-*•]|\d+[.)])\s+(.*)$/.exec(line);
    if (bullet) {
      if (!list) {
        list = { type: /^\s*\d/.test(line) ? "ordered" : "list", items: [] };
        blocks.push(list);
      }
      list.items.push(inlineTokens(bullet[1]));
      continue;
    }
    list = null;
    if (!line.trim()) continue;
    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    blocks.push(heading ? { type: "paragraph", tokens: [{ type: "bold", text: heading[1] }] } : { type: "paragraph", tokens: inlineTokens(line) });
  }
  return blocks;
}

export function conversationDate(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

// --- actions (V2): approval cards -----------------------------------------------------------------------------------------
// The card itself (title, before → after, consequences) is written by the server from the real record: the panel only shows it.

export const PROPOSAL_DONE = ["accepted", "refused", "expired", "failed", "cancelled"];

export function isPendingProposal(proposal) {
  return proposal?.status === "pending";
}

// The outcome line of a decided card, in the person's words.
// A BATCH card (several records on one card): the body of « Accepter » from what the person did on it — the files joined, the lines they
// unticked, the shared value only when they changed it. `undefined` when they did nothing (a plain accept).
export function acceptBody(card, { fileIds, excluded, commonValue } = {}) {
  const body = {};
  if (fileIds && card?.attachments) body.file_ids = fileIds;
  if (excluded && excluded.size > 0) body.exclude_ids = [...excluded];
  if (card?.common && commonValue !== undefined && String(commonValue).trim() !== String(card.common.value ?? "").trim()) body.common_value = String(commonValue).trim();
  return Object.keys(body).length ? body : undefined;
}

// « 3 sur 4 lignes » for a batch card; `selected` is what Accepter would run.
export function batchSelection(card, excluded) {
  const total = card?.items?.length || 0;
  const selected = card?.items ? card.items.filter((i) => !excluded.has(i.id)).length : 0;
  return { total, selected };
}

// Can Accepter be pressed on a batch card: at least one line kept, and the shared value (when it can be edited) still usable.
export function batchReady(card, excluded, commonValue) {
  if (!card?.items) return true;
  if (batchSelection(card, excluded).selected === 0) return false;
  if (card.common?.type === "date") return /^\d{4}-\d{2}-\d{2}$/.test(String(commonValue || ""));
  if (card.common?.field === "reason") return String(commonValue || "").trim().length >= 3;
  return true;
}

export function proposalOutcome(proposal) {
  switch (proposal?.status) {
    case "accepted":
      return { tone: "success", text: `Fait : ${proposal.result?.message || "action enregistrée"}.` };
    case "refused":
      if (proposal.refusal_reason?.startsWith("Enregistrée par la personne")) return { tone: "muted", text: "Enregistrée par vous dans le formulaire." };
      return { tone: "muted", text: proposal.refusal_reason ? `Refusée : « ${proposal.refusal_reason} »` : "Refusée." };
    case "expired":
      return { tone: "muted", text: "Expirée sans réponse : rien n'a été fait." };
    case "cancelled":
      return { tone: "muted", text: "Annulée par une nouvelle question : rien n'a été fait." };
    case "failed":
      return { tone: "error", text: proposal.error || "L'action n'a pas pu être enregistrée." };
    default:
      return null;
  }
}

// A conversation read back from the server: the decision notes are the server's own account for the assistant (the card already
// shows the decision), so they are not shown; each answer gets back the card it paused on.
export function restoreMessages(messages, proposals) {
  const byId = Object.fromEntries((proposals || []).map((p) => [p.id, p]));
  return (messages || [])
    .filter((m) => m.kind !== "decision")
    .map((m) => ({ id: m.id, role: m.role, text: m.text || "", errorCode: m.error_code || null,
      proposal: m.proposal_id ? byId[m.proposal_id] || null : null }));
}

export function pendingProposal(messages) {
  const found = [...(messages || [])].reverse().find((m) => isPendingProposal(m.proposal));
  return found ? found.proposal : null;
}

// « Modifier » : the usual task form, prefilled with what the assistant proposed (TaskFormSheet's own props).
export function taskFormProps(proposal) {
  const edit = proposal?.edit;
  if (!edit || edit.form !== "task") return null;
  const initial = edit.initial || {};
  if (edit.mode === "edit") return { task: { id: edit.task_id, ...initial }, initialValues: undefined };
  const pharmacy = (proposal.card?.fields || []).find((f) => f.label === "Pharmacie")?.after;
  return {
    task: null,
    initialValues: {
      title: initial.title || "", description: initial.description || "", due_date: initial.due_date || "",
      priority: initial.priority || "medium", task_type: initial.task_type || "autre", domain: initial.domain || "",
      prospect: initial.prospect_id ? { id: initial.prospect_id, pharmacy_name: pharmacy || initial.prospect_id } : null,
      assigned_to: initial.assigned_to || [],
    },
  };
}

// The page's welcome line: « Bonjour, Awa » / « Bonsoir, Awa » after 18 h, the first name only.
export function copilotGreeting(name, now = new Date()) {
  const first = (name || "").trim().split(/\s+/)[0];
  const hello = now.getHours() >= 18 || now.getHours() < 4 ? "Bonsoir" : "Bonjour";
  return first ? `${hello}, ${first}` : hello;
}

// The conversations whose title contains the search, accents and case ignored.
export function filterConversations(conversations, search) {
  const fold = (text) => (text || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const wanted = fold(search).trim();
  if (!wanted) return conversations;
  return (conversations || []).filter((c) => fold(c.title).includes(wanted));
}
